import { useEffect } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * A window's keyboard focus: it moves into the window as it opens, Tab and Shift+Tab stay
 * inside it, and it goes back to whatever had it (the button that opened it, when that is
 * still on the page) as the window closes.
 */
const useDialogFocus = (dialogRef, isOpen) => {
  useEffect(() => {
    const dialog = isOpen ? dialogRef.current : null;
    if (!dialog) return undefined;

    const opener = document.activeElement;
    dialog.focus({ preventScroll: true });

    const onKeyDown = e => {
      if (e.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll(FOCUSABLE)];
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener('keydown', onKeyDown);

    return () => {
      dialog.removeEventListener('keydown', onKeyDown);
      if (opener && opener !== document.body && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [isOpen, dialogRef]);
};

export default useDialogFocus;
